import type { FilterValue, TableState } from './state'
import type { ColumnDef, SortState } from './types'

/**
 * Per-column accessor used by client-side filtering/sorting. By default the
 * helper falls back to {@link ColumnDef.accessor} - which is fine when the
 * value rendered in the cell is already the value to filter/sort on.
 *
 * For columns that render JSX (e.g. badges) and need a raw scalar for
 * comparison, supply a custom `valueGetter` per column key.
 */
export interface ClientSideOptions<TRow> {
  /** Override per-column value getter for filter/sort. Receives the raw row. */
  valueGetters?: Partial<Record<string, (row: TRow) => unknown>>
  /** Custom searchable string for the global search box. Defaults to JSON-stringifying the row. */
  searchString?: (row: TRow) => string
}

/**
 * Apply {@link TableState} (search + filters + sort) to an in-memory dataset.
 * Pagination is NOT applied here - that's the caller's job (slice or push to server).
 */
export function applyClientSideTableState<TRow>(
  rows: TRow[],
  columns: ColumnDef<TRow>[],
  state: TableState,
  options: ClientSideOptions<TRow> = {},
): TRow[] {
  const colByKey = new Map(columns.map((c) => [c.key, c]))

  const getValue = (key: string, row: TRow): unknown => {
    const override = options.valueGetters?.[key]
    if (override) return override(row)
    const col = colByKey.get(key)
    if (!col) return undefined
    return col.accessor(row)
  }

  const search = state.search.trim().toLowerCase()
  const searchString =
    options.searchString ??
    ((r: TRow) => JSON.stringify(r).toLowerCase())

  let result = rows

  if (search) {
    result = result.filter((r) => searchString(r).includes(search))
  }

  for (const [key, value] of Object.entries(state.filters)) {
    result = result.filter((r) => matchesFilter(getValue(key, r), value))
  }

  if (state.sort.length > 0) {
    result = [...result].sort(makeComparator(state.sort, getValue))
  }

  return result
}

function matchesFilter(raw: unknown, filter: FilterValue): boolean {
  switch (filter.kind) {
    case 'text': {
      if (filter.value.trim() === '') return true
      const v = filter.value.toLowerCase()
      return String(raw ?? '').toLowerCase().includes(v)
    }
    case 'select': {
      if (filter.values.length === 0) return true
      return filter.values.includes(String(raw ?? ''))
    }
    case 'dateRange': {
      if (!filter.from && !filter.to) return true
      const s = String(raw ?? '')
      if (!s) return false
      if (filter.from && s < filter.from) return false
      if (filter.to && s > filter.to) return false
      return true
    }
    case 'bool': {
      if (filter.value === null) return true
      return Boolean(raw) === filter.value
    }
  }
}

function makeComparator<TRow>(
  sort: SortState,
  getValue: (key: string, row: TRow) => unknown,
): (a: TRow, b: TRow) => number {
  return (a, b) => {
    for (const c of sort) {
      const av = getValue(c.key, a)
      const bv = getValue(c.key, b)
      const cmp = compare(av, bv)
      if (cmp !== 0) return c.direction === 'asc' ? cmp : -cmp
    }
    return 0
  }
}

function compare(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0
  if (a == null) return -1
  if (b == null) return 1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)
  return String(a).localeCompare(String(b), 'pl', { numeric: true, sensitivity: 'base' })
}
